import * as React from "react";
import { useMemo } from "react";
import { ListBase, ListProps, useDataProvider, useListContext, usePermissions, useRefresh } from "react-admin";
import { Input, Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket } from "@fortawesome/free-solid-svg-icons";
import { useNotify } from "ra-core";
import { FilterField, SortOption, SortSelect } from "@src/components/Common/DataList";
import { NetOrcaTableListV1, TableListColumn } from "@src/components/Common/NetOrcaTableList";
import { LiveTableWrapper } from "@src/components/Common/tableUtils";
import { ModalWidth } from "@src/components/Common/Variables";
import { ExtendedDataProvider } from "@src/types";
import { canWritePack } from "@src/components/AIProcessors/packAccess";
import PackDashboardTabs from "../PackDashboard/PackDashboardTabs";
import PipelineRow from "./PipelineRow";
import "./Pipelines.css";

const resourcePack = "serviceowner_pack";

const formatStateLabel = (state: string): string => state
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const stateOptions = ['OK', 'FAILED', 'SCHEDULED', 'WAITING_FOR_RESPONSE']
    .map(state => ({ value: state, label: formatStateLabel(state) }));

/**
 * Ordering by id is ordering by when it ran: the ids are sequential, and the
 * created timestamp is worked out per pipeline from its parts, so the API has
 * no column for it. State and stage are not here either — both are things to
 * filter down to, not to order by.
 */
const sortOptions: SortOption[] = [
    { value: 'id:DESC', label: 'Newest first', field: 'id', order: 'DESC' },
    { value: 'id:ASC', label: 'Oldest first', field: 'id', order: 'ASC' },
];

const PipelineListView = () => {
    const { refetch, currentSort, setSort } = useListContext();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    /* Retriggering is a Pack write, and only ADMIN and CONFIGURATION may make
       one — the same rule the pipeline tab resolves access by. A role that
       cannot do it is not offered it: the API refuses the request anyway, so
       the button was only ever a way to earn a 403. */
    const canRetrigger = canWritePack(permissions);

    const retriggerAIProcessor = (service_item_id: number) => {
        const verb_trigger = `retrigger/service_item/${service_item_id}/`;
        let inputValue = '';

        Modal.confirm({
            title: 'Service Owner feedback:',
            icon: null,
            width: ModalWidth,
            style: { height: '70vh' },
            content:
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div>This feedback will be given to the AI model as context for the next loop</div>
                    <Input.TextArea rows={8} style={{ flex: 1 }} placeholder="Your comment" onChange={(e) => inputValue = e.target.value}/>
                </div>,
            onOk() {
                let payload = undefined;
                if (inputValue !== "")
                    payload = { serviceowner_comment: inputValue };
                dataProvider.createAction(resourcePack, { data: payload, verb: verb_trigger })
                    .then(() => {
                        refetch();
                        refresh();
                        notify('AI Processor is triggered');
                    })
                    .catch((error: any) => notify(JSON.stringify(error.body), 'error'));
            }
        });
    };

    // The scope of a pipeline is a service item, so the same cascade the change
    // instances list offers narrows this one: team, then application, then item.
    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'consumer_team_id',
            label: 'Consumer Team',
            type: 'select',
            resource: 'consumer_teams',
            dataCy: 'team-selector',
        },
        {
            name: 'application_id',
            label: 'Application',
            type: 'select',
            resource: 'owner_applications',
            dependsOn: ['consumer_team_id'],
            dataCy: 'application-selector',
        },
        {
            name: 'service_item_id',
            label: 'Service Item',
            type: 'select',
            resource: 'owner_service_items',
            dependsOn: ['application_id'],
            dataCy: 'service-item-selector',
        },
        {
            name: 'service_id',
            label: 'Service',
            type: 'select',
            resource: 'services',
            dataCy: 'service-selector',
        },
        {
            name: 'state',
            label: 'State',
            type: 'select',
            options: stateOptions,
            dataCy: 'state-filter',
        },
        {
            name: 'applied',
            label: 'Applied only',
            type: 'toggle',
            checkedValue: true,
            dataCy: 'applied-filter',
        },
    ], []);

    const columns: TableListColumn[] = useMemo(() => [
        {
            // its filter waits on an application being picked, which reads as
            // broken on a header — it goes in the panel, under its parent
            label: 'Service Item',
            icon: <FontAwesomeIcon icon={faRocket} />,
            dataCy: 'column-service-item',
        },
        { label: 'State', filters: ['state'], dataCy: 'column-state' },
        { label: 'Stage' },
        { label: 'Cost' },
        // the last track carries the filter trigger, over the row's actions
    ], []);

    return (
        <NetOrcaTableListV1
            className="netorca-pl"
            dataCy="pipeline-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--pl-cols)"
            banner={<PackDashboardTabs />}
            drawerTitle="Filter pipelines"
            emptyText="No pipelines found"
            noun="pipeline"
            // sorting shapes the list without filtering it, so it sits below the
            // fields rather than among them
            drawerFooter={
                <div className="ft-field">
                    <label className="ft-field__label" htmlFor="pl-sort">Sort</label>
                    <SortSelect id="pl-sort" options={sortOptions} currentSort={currentSort} setSort={setSort} />
                </div>
            }
            renderWrapper={(results) => <LiveTableWrapper refetch={refetch}>{results}</LiveTableWrapper>}
            renderRow={(record) => (
                <PipelineRow
                    key={record.id}
                    record={record}
                    onRetrigger={canRetrigger ? retriggerAIProcessor : undefined}
                />
            )}
        />
    );
};

const PipelineList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase sort={{ field: 'id', order: 'DESC' }} perPage={25} filter={{ light: true }} {...props}>
        <PipelineListView />
    </ListBase>
);

export default PipelineList;
